import React, { useEffect } from "react";
import ArticleTableOfContentsRecord from "models/view-models/table-of-contents/article-table-of-contents-record";
import PublicationComponentType from "utilities/enumerations/publication-component-type";
import NotebookTableOfContentsHeaderButton from "pages/notebooks/notebook-reference-panel/bookview/table-of-contents/notebook-table-of-contents-header-button";
import CollapsePanel from "molecules/collapse-panel/collapse-panel";
import { IconSizes } from "atoms/constants/icon-sizes";
import VirtualTableOfContentsArticleSections from "molecules/virtual-table-of-contents-items/virtual-table-of-contents-article-sections";
import { useOpenTocPanels } from "pages/notebooks/notebook-reference-panel/use-open-toc-panels";

interface VirtualTableOfContentsArticleProps {
    articles: ArticleTableOfContentsRecord[];
    chapterNfpaLabel: string;
    isPublic?: boolean;
    locale?: string;
    parentId?: string;
    publicationId: number;
}

const CSS_CLASS_NAME = "c-virtual-table-of-contents";

const VirtualTableOfContentsArticle: React.FunctionComponent<
    VirtualTableOfContentsArticleProps
> = (props: VirtualTableOfContentsArticleProps) => {
    const { articles, locale, parentId, chapterNfpaLabel, publicationId } =
        props;

    const sortedArticles = articles.sort(
        (a, b) => (a.displaySequence ?? 0) - (b.displaySequence ?? 0)
    );

    const { isPanelCollapsed } = useOpenTocPanels();

    useEffect(() => {
        const scrollToOpenArticle = () => {
            articles.some((article) => {
                if (
                    !isPanelCollapsed(
                        article.id!,
                        PublicationComponentType.Article
                    )
                ) {
                    const articleElement = document.getElementById(
                        `${article.id}_${PublicationComponentType.Article}`
                    );
                    articleElement?.scrollIntoView({
                        block: "start",
                    } as ScrollIntoViewOptions);
                    return true;
                }
                return false;
            });
        };
        scrollToOpenArticle();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <nav className={`${CSS_CLASS_NAME} -article`}>
            <ul className={`${CSS_CLASS_NAME}__list`}>
                {sortedArticles.map((article) => (
                    <CollapsePanel
                        id={`${article.id}_${PublicationComponentType.Article}`}
                        iconSize={IconSizes.Base}
                        data-test-id="chapter-collapse-panel"
                        panelTop={
                            <NotebookTableOfContentsHeaderButton
                                dataTestDisplaySequence={
                                    article.displaySequence
                                }
                                displayTitle={
                                    <span>
                                        <strong>
                                            {article.getLabel(locale)}
                                        </strong>{" "}
                                        {article.getTitle(locale)}
                                    </span>
                                }
                                caapsId={article.id}
                                publicationComponentType={
                                    PublicationComponentType.Article
                                }
                                parentId={parentId}
                            />
                        }
                        collapse={isPanelCollapsed(
                            article.id!,
                            PublicationComponentType.Article
                        )}
                        buttonAriaText={article.getDisplayTitle(locale)}>
                        <VirtualTableOfContentsArticleSections
                            chapterNfpaLabel={chapterNfpaLabel}
                            article={article}
                            publicationId={publicationId}
                        />
                    </CollapsePanel>
                ))}
            </ul>
        </nav>
    );
};

export default VirtualTableOfContentsArticle;
